import React, { forwardRef } from 'react';
import { CONTROL_HEIGHT } from '../../constants/inputs';

/**
 * The sticky page-toolbar shell. One definition of the bar that sits above a
 * long list — Breakdown, Manage Teams, My Tasks today; the order-list pages in
 * phase 2.
 *
 * Extracted from `GagBreakdown.js` on 2026-08-08, where the identical class
 * string had already been copied into `TeamManage.js` and `MyTeamTasks.js`.
 *
 * ── THE Z-INDEX CONTRACT — preserve it or dropdowns render behind content ──
 * `z-20` is not decoration. There are three tiers and they must stay in this
 * order:
 *   z-10  sticky GROUP / SECTION headers inside the list
 *   z-20  THIS bar — it has to paint over those headers as they scroll under it
 *   z-50  dropdown PANELS (`components/ui/Dropdown.js`), which open INSIDE this
 *         bar's stacking context, so they are above everything this bar covers
 * z-60 is THE ESCAPE TIER: anything PORTALLED to document.body because it
 * cannot win where it sits. Two users since 2026-08-09 — GagGroupTable's kebab
 * and ui/Dropdown's opt-in `portal` prop (the Breakdown's inline Scenes
 * picker). Both write it as an inline `zIndex: 60`, not a Tailwind class.
 * 🚨 Reach for it only when an ancestor of the trigger CLIPS. The Scenes bug
 * looked like a z-index bug and was not one: measured, the panel's z-50
 * already outranked everything and it was being clipped by a `truncate`d <td>.
 * z-index orders what is PAINTED; it cannot un-clip. See ui/Dropdown's
 * docblock for the measurements. Lower this
 * bar's z-index and the group headers scroll over it; raise it above the
 * portal layer and the kebab menu disappears behind it.
 *
 * ── STICKY OFFSET ──
 * `top-0 lg:top-14` pins the bar under the Layout desktop title bar, which only
 * exists at `lg` (56px — `APP_BAR_LG_PX` in `hooks/useStickyToolbar.js`).
 * These are STATIC classes on purpose: the bar must paint correctly without
 * waiting on JS. Only a second-level sticky header needs the measured value,
 * which is what `useStickyToolbar()`'s `headerTop` is for — pass this
 * component the hook's `toolbarRef` and it measures itself.
 *
 * ── PHASE 2 NOTE ──
 * An active-filter chip row can live here without any change to this shell.
 * Today every chip row is `components/ui/FilterChips` (the last two inline
 * hand-copies, on My Tasks and Manage Teams, were migrated 2026-08-08) and it
 * renders
 * as a SIBLING below the bar, i.e. they scroll away; because the shell is
 * `flex-wrap`, a chip row passed as the last child with `w-full` instead wraps
 * onto its own row INSIDE the sticky bar and stays pinned. Both work — that is
 * a product choice, not a structural one.
 *
 * Props:
 *  - children     — the toolbar controls, left to right
 *  - rightSlot    — pinned to the right edge (`ml-auto`); omit for none
 *  - marginClassName — bottom spacing. A SEPARATE slot rather than something
 *    you append via `className`, because two conflicting Tailwind utilities on
 *    one element (`mb-3 mb-2`) resolve by stylesheet order, not by the order
 *    you wrote them — the same shorthand/longhand trap documented in
 *    `constants/inputs.js`. Pass the one you want; do not pass two.
 *  - className    — extra NON-CONFLICTING classes
 *  - ...rest      — spread onto the element (e.g. `aria-label`)
 */
/**
 * 🔻 **`gap-x-4 gap-y-2` — GROUPING BY GAP, NOT AN ARBITRARY SPACING TWEAK.**
 * This was an even `gap-2` throughout, and an even gap is what makes a bar look
 * *assembled* rather than *composed*: with every neighbour the same distance
 * away, nothing tells the eye which controls belong together. The rule now is
 *   tight WITHIN a group (8px, `gap-2`, owned by each group's own wrapper)
 *   wider BETWEEN groups (16px, `gap-x-4`, owned by this shell)
 * — so **a direct child of `<Toolbar>` is a GROUP, not a control.** A page that
 * drops six loose controls in here gets six equal 16px gaps and undoes the whole
 * device; wrap them in `<div className="flex items-center gap-2">` clusters
 * first.
 *
 * `gap-y-2` stays at 8px deliberately: the vertical gap only exists once the bar
 * has wrapped, and a 16px vertical rhythm there makes a two-row bar noticeably
 * taller for no grouping benefit — rows are already separated by being rows.
 */
export const TOOLBAR_SHELL =
  'flex flex-wrap items-center gap-x-4 gap-y-2 sticky top-0 lg:top-14 z-20 py-2 ' +
  'bg-gray-100 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700';

/** The right-pinned cluster — one GROUP, so it keeps the tight within-group gap.
 *  `flex-wrap` so a crowded cluster stacks rather than overflowing the bar. */
export const TOOLBAR_RIGHT_SLOT = 'ml-auto flex flex-wrap items-center gap-2';

/** A cluster of related controls inside the bar. Use this for every direct child
 *  of `<Toolbar>` that is more than one control — see `TOOLBAR_SHELL` above for
 *  why the shell's own gap is the BETWEEN-group one. */
export const TOOLBAR_GROUP = 'flex flex-wrap items-center gap-2';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE THREE WEIGHTS — what makes a toolbar read as designed rather than tidy
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Product owner, 2026-08-08, of the Breakdown bar — the one he had just called
 * his favourite: *"still needs work to look professional tho"*.
 *
 * The diagnosis, which is the part worth carrying: **every control carried the
 * same visual weight.** Search box, `Group ▾`, `Filters ▾`, `Views ▾`, `Export`,
 * `+ New Gag` — all bordered, all filled, all the same size, evenly spaced. The
 * eye had nothing to land on and no grouping to read, so the bar looked like a
 * row of undifferentiated boxes. **That is independent of height and alignment**,
 * which is why levelling the heights alone did not get there.
 *
 * Three weights, and a page uses all three or fewer, never a fourth:
 *
 *  1. **INPUT** — the search box (`SEARCH_INPUT_FIELD`, `constants/inputs.js`).
 *     Keeps a real border and a real field background. It is the only thing on
 *     the bar you TYPE into and it should look like it; a ghost text field is an
 *     accessibility and discoverability problem, not a style.
 *
 *  2. **GHOST** — `TOOLBAR_GHOST` below. Every disclosure trigger (`Sort ▾`,
 *     `Filters ▾`, `Group ▾`, `Views ▾`) and every secondary action (`Export`,
 *     `Refresh`). No resting border, no resting fill; both appear on hover,
 *     focus-visible and while open.
 *
 *  3. **FILLED** — `BTN.primary`, and **EXACTLY ONE PER BAR**: the page's primary
 *     create. `+ New Gag` on the Breakdown. 🔑 **A page with no primary create
 *     has NO filled element, and that is the correct outcome — do not promote
 *     something else to fill the slot.** The whole value of the filled weight is
 *     that there is one of it; two filled controls is the undifferentiated bar
 *     again with extra colour.
 *
 * Anything that is INFORMATION rather than a control (the Breakdown's est-cost
 * chip) stays muted and must not compete with the filled action.
 */

/**
 * The GHOST weight: a bar control that is quiet at rest and fully affordant the
 * moment it is engaged. Same size and same hit area as a bordered control —
 * **the ghost treatment costs no width**, which is why it does not change any
 * one-row arithmetic.
 *
 * 🚨 **`border border-transparent` AT REST, NEVER `border-0`.** The border box is
 * reserved at rest and only its COLOUR changes, so the control does not grow by
 * 2px and shove its neighbours sideways the instant a pointer touches it. That
 * jump is the classic ghost-button bug and it is invisible in a static
 * screenshot.
 *
 * 🚨 **GHOST MUST NOT COST DISCOVERABILITY — three states, not one:**
 *  - `hover:` for the pointer.
 *  - `focus-visible:` for the keyboard, which never hovers. It gets BOTH the
 *    border/fill and a ring, because a ring alone on a borderless control reads
 *    as a focus artefact rather than as "this is a button".
 *  - `aria-expanded:` for a disclosure that is OPEN. `ui/Dropdown` already sets
 *    `aria-expanded` on its trigger, so the open state is styled off the same
 *    attribute the accessibility tree reads — one source of truth, and it cannot
 *    drift from the panel's actual visibility. (Tailwind 3.2+ `aria-*` variant;
 *    this repo is on 3.4.19.) A plain `<button>` never sets it, so the variant is
 *    simply inert on `Export` / `Refresh`.
 *
 * ♿ **RESTING CONTRAST, MEASURED against the bar's own background** — CLAUDE.md's
 * lesson from the actionable chevron is that a colour carrying meaning needs its
 * contrast checked, and a ghost control's RESTING label is exactly that case:
 *  - light: `text-gray-700` #374151 on the shell's `bg-gray-100` #F3F4F6 →
 *    **9.33:1** (WCAG AA text needs 4.5, AAA needs 7).
 *  - dark:  `text-gray-200` #E5E7EB on the shell's `dark:bg-gray-900` #111827 →
 *    **14.24:1**.
 * Both are label text, not a UI-component boundary, so 1.4.3 applies rather than
 * 1.4.11 — and the resting state deliberately has no boundary to measure. The
 * hover/focus/open border is `gray-300` / `dark:gray-600`, which is decorative
 * reinforcement of a state the ring already carries.
 *
 * ⚠️ Every one of these states has a `dark:` pair. A border that appears only in
 * light mode is worse than no border at all — it makes the control look broken in
 * exactly one theme, which is the hardest kind of report to act on.
 */
export const TOOLBAR_GHOST =
  `inline-flex items-center justify-center gap-1.5 ${CONTROL_HEIGHT} px-4 py-2 rounded-lg text-sm font-medium ` +
  'border border-transparent bg-transparent text-gray-700 dark:text-gray-200 transition-colors ' +
  'hover:bg-white hover:border-gray-300 dark:hover:bg-gray-700 dark:hover:border-gray-600 ' +
  'aria-expanded:bg-white aria-expanded:border-gray-300 dark:aria-expanded:bg-gray-700 dark:aria-expanded:border-gray-600 ' +
  'focus:outline-none focus-visible:bg-white focus-visible:border-gray-300 ' +
  'dark:focus-visible:bg-gray-700 dark:focus-visible:border-gray-600 ' +
  'focus-visible:ring-2 focus-visible:ring-blue-500 ' +
  'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-transparent';

/**
 * THE trigger style for a `components/ui/Dropdown` disclosure that sits ON this
 * bar — `Sort ▾` and `Filters ▾` on the order pages, `Group ▾` / `Filters ▾` /
 * `Views ▾` on the Breakdown.
 *
 * Extracted here on 2026-08-08, when `Sort ▾` became the second one. It lives in
 * this file rather than `constants/buttons.js` because it is toolbar furniture,
 * not a button INTENT: `buttons.js` is organised by what an action MEANS
 * (primary / confirm / danger), and "disclosure on a toolbar" is not one of
 * those. This file already owns the bar's other class strings, so they sit
 * together.
 *
 * 🔑 **EVERY DISCLOSURE ON EVERY BAR MUST LOOK IDENTICAL, which is the whole
 * reason this is shared rather than copied.** They are the same kind of control
 * doing the same kind of thing next to each other on one row; two hand-tuned
 * near-copies drift on the first edit to either, and the drift shows up as one
 * of the two reading as a different class of control. `OrderFilterBar.js` held
 * the only copy (as `FILTERS_TRIGGER_CLS`) until `OrderSearchAndSort.js` needed
 * it too — the Code Unification Principle's "if it doesn't exist but the same
 * thing exists in another file, extract it FIRST, then import".
 * ⚠️ The Breakdown's three triggers were on `BTN.secondary` instead and so were
 * a second near-copy in all but name (38px vs 36px, a different fill). They now
 * take this. **Do not send a toolbar disclosure back to `BTN`.**
 *
 * ── 🔻 IT IS A **GHOST** CONTROL SINCE 2026-08-08 ──
 * It was `border-2 border-gray-300 bg-white`, i.e. exactly as loud as everything
 * else on the bar. `font-semibold` overrides the ghost's `font-medium` because a
 * disclosure names a piece of STATE the user has set (`Sort · Date ↓`,
 * `Filters (2)`), which wants slightly more weight than a bare action verb —
 * this is the one deliberate divergence from `TOOLBAR_GHOST`, and it is a type
 * weight, not a border.
 */
export const TOOLBAR_DISCLOSURE_TRIGGER = `${TOOLBAR_GHOST} font-semibold`;

/**
 * ── SEGMENTED CONTROLS: A `p-1` TRACK WITH PILL SEGMENTS ──
 *
 * The Breakdown's `Table | Timeline | Labour` switcher is the pattern, and it is
 * the pattern because the product owner named it: *"breakdown is probably the
 * best looking menu structure"*. `All | Actionable` and `Cards | Table` on the
 * order pages were two DETACHED bordered buttons with a gap between them, which
 * both looked ragged and misstated the semantics — they are radios, mutually
 * exclusive, exactly one active, and two detached buttons read as two
 * independent toggles that happen to sit together.
 *
 * The track is a tinted recess; the active segment is a pill floating in it. That
 * is what carries "these options are alternatives to each other" without a word
 * of caption, and it is why the `View` caption could be deleted from the layout
 * toggle rather than restyled.
 *
 * 🔑 The track owns the HEIGHT (`CONTROL_HEIGHT`) and the segments `items-stretch`
 * into it, so a segment's own type size cannot change the control's height —
 * which is precisely how `Cards | Table` ended up 8px shorter than its
 * neighbours by carrying `text-xs`.
 *
 * ⚠️ `p-1` inside `CONTROL_HEIGHT` means the segments are 38 − 8 = **30px**. That
 * is 2px shorter than the Breakdown's segments are today (its track was 40px),
 * and it is the one place where adopting the shared height changes the exemplar.
 */
export const TOOLBAR_SEGMENT_TRACK =
  `inline-flex items-stretch gap-1 ${CONTROL_HEIGHT} p-1 rounded-lg bg-gray-100 dark:bg-gray-800`;

export const TOOLBAR_SEGMENT =
  'inline-flex items-center justify-center px-3 rounded-lg text-sm font-semibold transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500';

/**
 * COMPACT pair, 30px from `md` up (26px segments) against the standard 38px.
 * TRIAL 2026-10-13 (owner: "try reducing the height on the labour flow first so
 * I can see if it works at the more compact height"): the Labour Flow's Sort and
 * Zoom only. Below `md` it keeps the 44px tap target. Its own strings rather
 * than overrides, because two Tailwind sizes on one element resolve by
 * stylesheet order. `_ON` / `_OFF` are shared with the standard pair.
 */
export const TOOLBAR_SEGMENT_TRACK_COMPACT =
  'inline-flex items-stretch gap-0.5 min-h-[44px] md:min-h-0 md:h-[1.875rem] p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800';

export const TOOLBAR_SEGMENT_COMPACT =
  'inline-flex items-center justify-center px-2.5 rounded-md text-xs font-semibold transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500';

/** Inactive segment: no fill at rest, a raised one on hover — the segment lifts
 *  out of the track toward where the active pill sits. */
export const TOOLBAR_SEGMENT_OFF =
  'text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700';

/** Active segment. The house badge/pill pattern, both variants (CLAUDE.md →
 *  Colour Theme & Dark Mode). A control whose active state needs a DIFFERENT
 *  hue — `Actionable` is green — supplies its own in the same shape. */
export const TOOLBAR_SEGMENT_ON =
  'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';

/**
 * ── A `constants/buttons.js` BTN SITTING ON THIS BAR NEEDS `CONTROL_HEIGHT` TOO ──
 *
 * Only the FILLED control still takes a `BTN` on a toolbar — `+ New Gag` uses
 * `BTN.primary`. Everything that used to take `BTN.secondary` here is now ghost.
 * `BTN.primary` is `px-4 py-2 text-sm border` = **38px**, which happens to equal
 * `CONTROL_HEIGHT` today; append it anyway, because that equality is a
 * coincidence of two independently-chosen numbers and nothing keeps them in step:
 *
 *   className={`${BTN.primary} ${CONTROL_HEIGHT}`}
 *
 * ⚠️ **AN OVERRIDE AT THE CALL SITE, NEVER A CHANGE TO `BTN`.** Those intents are
 * used app-wide, on forms, modals and panels that have nothing to do with a
 * toolbar, so re-tuning their geometry to level one bar would be a site-wide
 * visual change arriving through a shared constant — the worst way for one to
 * arrive. The height belongs where the bar is, applied only to controls on it.
 *
 * 🪤 And note WHY this composes safely rather than by luck: `BTN` sets padding
 * (`py-2`) and never sets a height at any breakpoint, while `CONTROL_HEIGHT` sets
 * `md:h-[2.375rem]` and never sets padding. There is no same-property conflict,
 * so the `mb-3 mb-2` stylesheet-order trap documented on `marginClassName` above
 * cannot fire here. Do not "simplify" this by putting a bare height in either
 * string.
 */

const Toolbar = forwardRef(function Toolbar(
  { children, rightSlot, marginClassName = 'mb-3', className = '', ...rest },
  ref
) {
  return (
    <div ref={ref} className={`${TOOLBAR_SHELL} ${marginClassName} ${className}`.trim()} {...rest}>
      {children}
      {rightSlot ? <div className={TOOLBAR_RIGHT_SLOT}>{rightSlot}</div> : null}
    </div>
  );
});

export default Toolbar;
